import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { DashboardLayout } from "components/DashboardLayout";
import { TableUI } from "components/design/TableUI";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useModal } from "hooks/useModal";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useDebounce } from "utils/debounce";
import { MouseEvent } from "react";
import { useHistory } from "react-router-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  AdminCoupon,
  AdminCouponListItem,
  useGetAdminCouponsQuery,
} from "store/apis/couponAdmin";
import { CouponFormModal } from "./CouponFormModal";
import { formatDate, getCouponSignupLink } from "./couponUtils";

export const CouponStatusTag = ({ coupon }: { coupon: AdminCoupon }) => {
  if (!coupon.is_active) {
    return <Tag tagType="gray">Inactive</Tag>;
  }
  return coupon.is_valid ? (
    <Tag tagType="green">Live</Tag>
  ) : (
    <Tag tagType="orange">Outside validity</Tag>
  );
};

export const Coupons = () => {
  usePageTitle("Coupons");
  const history = useHistory();
  const query = useQuery();
  const { update, updateMultiple } = useUpdateQuery();
  const { copyToClipboard } = useCopyToClipboard();
  const createModal = useModal();

  const searchValue = query.get("search");
  const searchTerm = useDebounce(searchValue);
  const pageNumber = Number(query.get("page")) || 1;

  const { data, isLoading, isSuccess } = useGetAdminCouponsQuery({
    search_term: searchTerm || null,
    page_num: pageNumber,
  });

  const {
    coupons = [],
    total_count: totalCount = 0,
    total_pages: totalPages = 1,
    per_page: perPage = 25,
  } = data || {};

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateMultiple([
      { query: "search", value: e.target.value || null },
      { query: "page", value: null },
    ]);
  };

  const onPageNumChange = (page: number) => {
    update({ query: "page", value: page });
  };

  const onCopyLink = (e: MouseEvent, couponCode: string) => {
    e.stopPropagation();
    copyToClipboard(getCouponSignupLink(couponCode));
  };

  const columnHelper = createColumnHelper<AdminCouponListItem>();

  const columns = [
    columnHelper.accessor("coupon_code", {
      header: "Code",
      size: 14,
      cell: ({ row }) => (
        <span className="t-font-medium t-text-purple">
          {row.original.coupon_code}
        </span>
      ),
    }),
    columnHelper.accessor("description", {
      header: "Description",
      size: 26,
      cell: ({ row }) => (
        <span className="t-truncate t-block">
          {row.original.description || "—"}
        </span>
      ),
    }),
    columnHelper.display({
      id: "validity",
      header: "Validity",
      size: 20,
      cell: ({ row }) =>
        `${formatDate(row.original.valid_from)} – ${formatDate(
          row.original.valid_till
        )}`,
    }),
    columnHelper.display({
      id: "mappings",
      header: "Task / Stripe",
      size: 12,
      cell: ({ row }) =>
        `${row.original.task_category_mapping_count} / ${row.original.stripe_coupon_count}`,
    }),
    columnHelper.display({
      id: "status",
      header: "Status",
      size: 14,
      cell: ({ row }) => <CouponStatusTag coupon={row.original} />,
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 14,
      cell: ({ row }) => (
        <Button
          size="small"
          onClick={(e) => onCopyLink(e, row.original.coupon_code)}
        >
          Copy signup link
        </Button>
      ),
    }),
  ];

  const table = useReactTable({
    data: coupons,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 1 },
  });

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title="Coupons"
          right={
            <Button
              customType="primary"
              onClick={createModal.open}
              size="small"
            >
              Create coupon
            </Button>
          }
          bottom={
            <div className="t-flex t-justify-between t-items-center t-pb-4">
              <div className="t-w-60">
                <Search
                  block
                  onChange={onSearch}
                  value={searchValue || ""}
                  placeholder="Search code or description"
                />
              </div>
              <PaginationHandler
                page_num={pageNumber}
                per_page={perPage}
                total_count={totalCount}
                total_pages={totalPages}
                onPageNumChange={onPageNumChange}
                isLoading={isLoading}
              />
            </div>
          }
        />
      }
      contentProps={{ className: "t-flex-1 t-min-h-0 t-flex t-flex-col" }}
    >
      <div className="t-flex-1 t-min-h-0 t-overflow-auto">
        <Async.Root
          isLoading={isLoading}
          isEmpty={totalCount === 0}
          isSuccess={isSuccess}
        >
          <Async.Empty>
            <EmptyScreen text="No coupons found" />
          </Async.Empty>
          <Async.Success>
            <div className="t-h-full sm:t-min-w-[800px]">
              <TableUI
                table={table}
                layout="fixed"
                sticky
                height="100%"
                onRowClick={(row) =>
                  history.push(`/admin/coupons/${row.original.uuid}`)
                }
              />
            </div>
          </Async.Success>
        </Async.Root>
      </div>

      <CouponFormModal isOpen={createModal.isOpen} close={createModal.close} />
    </DashboardLayout>
  );
};
